import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

const GEMINI_API_KEY = 'AIzaSyADS1G1LkFBevYoTp0aUMmhcdxzXgtgGhQ';
const CHAT_HISTORY_KEY = 'chat_history';
const MAX_HISTORY_LENGTH = 10; // Maximum number of messages to store

const SYSTEM_INSTRUCTION = `คุณคือเจ้าหน้าที่ของสหกรณ์ออมทรัพย์กรมส่งเสริมการเกษตร จำกัด เป็นผู้ชาย คุณมีหน้าที่ให้ข้อมูลและคำแนะนำเกี่ยวกับ:
1. เงินฝาก - ประเภทเงินฝาก อัตราดอกเบี้ย เงื่อนไขการฝาก
2. เงินกู้ - ประเภทเงินกู้ อัตราดอกเบี้ย เงื่อนไขการกู้
3. ดาวน์โหลด - แบบฟอร์มและเอกสารต่างๆ
4. ผลการดำเนินงาน - รายงานผลการดำเนินงานของสหกรณ์
5. คลังความรู้ - ข้อมูลความรู้เกี่ยวกับการออมและการกู้ยืม
6. ระเบียบ/ข้อบังคับ - กฎระเบียบและข้อบังคับของสหกรณ์

คุณควรตอบคำถามด้วยความเป็นมิตร เป็นทางการ และให้ข้อมูลที่ถูกต้องครบถ้วน หากไม่แน่ใจในคำตอบ ควรแจ้งให้ผู้ถามทราบและแนะนำให้ติดต่อเจ้าหน้าที่โดยตรง`;

function ChatModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    // Load messages from localStorage on initial render
    const savedMessages = localStorage.getItem(CHAT_HISTORY_KEY);
    return savedMessages ? JSON.parse(savedMessages) : [];
  });
  const [input, setInput] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentUtterance, setCurrentUtterance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(messages));
  }, [messages]);

  const toggleOpen = () => setIsOpen(!isOpen);

  const handleSpeak = (text) => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setCurrentUtterance(null);
    } else {
      const utterance = new SpeechSynthesisUtterance(text.replace(/\*/g, ''));
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.lang = 'th-TH';
      
      utterance.onend = () => {
        setIsSpeaking(false);
        setCurrentUtterance(null);
      };

      window.speechSynthesis.speak(utterance);
      setCurrentUtterance(utterance);
      setIsSpeaking(true);
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    
    const userMsg = { sender: 'user', text: input };
    setMessages(prev => {
      const newMessages = [...prev, userMsg];
      // Keep only the last MAX_HISTORY_LENGTH messages
      return newMessages.slice(-MAX_HISTORY_LENGTH);
    });
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [
              { text: SYSTEM_INSTRUCTION },
              { text: `ผู้ใช้: ${input}` }
            ]
          }]
        }),
      });

      const data = await response.json();
      const botResponse = data.candidates[0].content.parts[0].text;
      
      const botMsg = { sender: 'bot', text: botResponse };
      setMessages(prev => {
        const newMessages = [...prev, botMsg];
        // Keep only the last MAX_HISTORY_LENGTH messages
        return newMessages.slice(-MAX_HISTORY_LENGTH);
      });
    } catch (error) {
      console.error('Error:', error);
      const errorMsg = { sender: 'bot', text: 'ขออภัย มีข้อผิดพลาดเกิดขึ้น กรุณาลองใหม่อีกครั้ง' };
      setMessages(prev => {
        const newMessages = [...prev, errorMsg];
        // Keep only the last MAX_HISTORY_LENGTH messages
        return newMessages.slice(-MAX_HISTORY_LENGTH);
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {isOpen && (
        <div className="fixed bottom-32 right-8 w-[90%] sm:w-[500px] bg-white rounded-xl shadow-2xl flex flex-col border-2 border-green-600">
          <div className="flex justify-between items-center p-4 bg-green-600 rounded-t-xl">
            <div className="flex items-center space-x-3">
              <img src="/มาสคอส.png" alt="Mascot" className="w-8 h-8 sm:w-12 sm:h-12 rounded-full border-2 border-white" />
              <h3 className="text-xl sm:text-2xl font-bold text-white">Chat Assistant</h3>
            </div>
            <button onClick={toggleOpen} className="text-white hover:text-gray-200">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-8 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-grow p-4 sm:p-6 overflow-y-auto space-y-4 max-h-[60vh] sm:max-h-[500px]">
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] sm:max-w-[80%] p-3 sm:p-4 rounded-xl text-base sm:text-lg ${
                  msg.sender === 'user' 
                    ? 'bg-green-600 text-white rounded-tr-none' 
                    : 'bg-gray-100 text-gray-800 rounded-tl-none'
                }`}>
                  {msg.sender === 'bot' ? (
                    <div className="space-y-2">
                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                      <button
                        onClick={() => handleSpeak(msg.text)}
                        className="mt-2 text-sm text-gray-600 hover:text-green-600"
                      >
                        {isSpeaking && currentUtterance?.text === msg.text ? (
                          <span className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            หยุด
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                            </svg>
                            พูด
                          </span>
                        )}
                      </button>
                    </div>
                  ) : (
                    msg.text
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 p-3 sm:p-4 rounded-xl rounded-tl-none">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="p-3 sm:p-4 border-t-2 border-gray-200">
            <div className="flex space-x-2 sm:space-x-3">
              <input
                type="text"
                className="flex-grow border-2 border-gray-300 rounded-xl px-3 sm:px-4 py-2 sm:py-3 text-base sm:text-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Type your message..."
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              />
              <button 
                onClick={sendMessage}
                disabled={isLoading}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      <button
        onClick={toggleOpen}
        className="fixed bottom-8 right-8 w-16 h-16 sm:w-24 sm:h-24 rounded-full shadow-2xl hover:shadow-3xl transition-all transform hover:scale-105 group"
      >
        <div className="relative w-full h-full">
          <img 
            src="/มาสคอส.png" 
            alt="Chat Assistant" 
            className="w-full h-full rounded-full object-cover border-4 border-green-600"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <p className="text-white text-xs sm:text-sm font-bold text-center px-2">
              ยินดีให้คำปรึกษา<br />คลิกเลย
            </p>
          </div>
        </div>
      </button>
    </div>
  );
}

export default ChatModal;
